import { AutoComplete, Form, Input } from 'antd';
import { ARRANGEMENT_LIMITS } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C4a §4] A DUTY POINT'S ZONE — ONE FREE-TEXT INPUT THAT SUGGESTS THE ZONES THIS ORDER ALREADY USES.
 *
 *  ⚠️ THE SUGGESTIONS ARE THE OTHER CARDS' ZONES AND NOTHING ELSE — no zone master exists and none is
 *  created here. `DutyPointStack` computes them from the watched drafts (`zoneOptionsFor`), so a zone
 *  typed on card 2 is offered on card 3 before anything is saved.
 *
 *  ⚠️ BOUND THROUGH `Form.Item`, NEVER `setFieldValue`. rc-field-form fires `onValuesChange` only from
 *  a registered control's own change, so a `Form.Item`-bound input arms autosave by itself — the
 *  mechanism [C2b §8c] records five controls losing work to.
 *
 *  ⚠️ `aria-label` ON THE CHILD `Input`, NOT ON `AutoComplete` — `TimeField`'s pattern: the input is
 *  the element with the accessible role, and the wrapper passes an unknown prop nowhere useful.
 *
 *  ⚠️ `readOnly`, NEVER `structureLocked`. `assertPublishedDutyPointsUnchanged` freezes places,
 *  schedule and policy version; a zone is none of those, so it stays editable on a live order.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const ZoneField = ({
  index,
  options,
  disabled,
  label,
  placeholder,
}: {
  index: number;
  options: readonly string[];
  disabled: boolean;
  label: string;
  placeholder: string;
}) => (
  <Form.Item name={['dutyPoints', index, 'zone']} label={label} className="mb-0 min-w-48 max-w-xs">
    <AutoComplete
      options={options.map((zone) => ({ value: zone }))}
      filterOption={(input, option) =>
        String(option?.value ?? '')
          .toLowerCase()
          .includes(input.trim().toLowerCase())
      }
      disabled={disabled}
      data-testid={`duty-point-zone-${String(index)}`}
    >
      <Input aria-label={label} maxLength={ARRANGEMENT_LIMITS.ZONE} placeholder={placeholder} allowClear />
    </AutoComplete>
  </Form.Item>
);
